import { BehaviorSubject, Observable, Subject, skip } from 'rxjs';
import { distinctUntilChanged, debounceTime, takeUntil, tap, filter } from 'rxjs/operators';
import { Component } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import { StateReset } from 'ngxs-reset-plugin';
import { routeNames } from '@shared/models';
import { FileState } from '@maz/store';
import { PortalService } from '@app/services';
import { ListState } from '@app/store/list.state';
import { PortalState } from '@app/store/portal.state';
import * as actions from '@app/store/list.actions';

@Component({
  selector: 'prp-list-search',
  templateUrl: './list-search.component.html',
  styleUrls: ['./list-search.component.scss'],
})
export class ListSearchComponent {
  @Select(ListState.searchQueryTitle)
  queryTitle$: Observable<string>;
  @Select(ListState.searchQueryEpisodeNr)
  queryEpisode$: Observable<string>;
  @Select(ListState.searchQueryMazId)
  queryMaz$: Observable<string>;
  @Select(ListState.searchLoading)
  loading$: Observable<boolean>;
  @Select(PortalState.rtl2Employee)
  producer$: Observable<boolean>;

  queryTitleChanged$ = new BehaviorSubject<string>('');
  queryMazChanged$ = new BehaviorSubject<string>('');
  timeoutSearch;
  private destroy$ = new Subject<void>();

  constructor(private store: Store, private portalService: PortalService) {
    this.queryTitleChanged$
      .pipe(
        skip(1),
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        tap((v: string) => this.store.dispatch(new actions.TitleSearch(v)))
      )
      .subscribe();

    this.queryMazChanged$
      .pipe(
        skip(1),
        takeUntil(this.destroy$),
        debounceTime(300),
        // Start search after 3 characters
        filter((v: string) => v?.length >= 3),
        tap((v: string) => this.store.dispatch(new actions.MazCardSearch(v)))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trigger() {
    this.store.dispatch([
      new StateReset(FileState),
      new Navigate([routeNames.editorBase], null, {
        queryParamsHandling: null,
      }),
    ]);
  }

  private search(callback: any, event?: any) {
    clearTimeout(this.timeoutSearch);

    this.timeoutSearch = setTimeout(() => {
      const pending = this.store.selectSnapshot(ListState.newFilePending);
      const dirty = this.store.selectSnapshot(FileState.formDirty);

      if (pending) {
        this.portalService.checkNewFilePending([event], callback);
      } else if (dirty) {
        this.portalService.checkChangesPending([event], callback);
      } else if (this.portalService.mazCardSelected) {
        this.portalService.newSearch([event], callback);
      } else {
        callback(event);
      }
    }, 500);
  }

  searchFormat(event) {
    if (event === this.queryTitleChanged$.value) return;
    if (!event?.length) {
      this.removeText();
      return;
    }
    const callback = this.triggerSearchFormat.bind(this);
    this.search(callback, event);
  }

  private triggerSearchFormat(event) {
    this.trigger();
    this.queryTitleChanged$.next(event);
  }

  searchMaz(event) {
    if (event === this.queryMazChanged$.value) return;
    if (!event?.length) {
      this.removeText();
      return;
    }
    const callback = this.triggerSearchMaz.bind(this);
    this.search(callback, event);
  }

  private triggerSearchMaz(event) {
    this.trigger();
    this.queryMazChanged$.next(event);
  }

  private removeText() {
    const callback = this.trigger.bind(this);
    this.search(callback);
  }
}
